import type { CSVCategory, MediaFile } from "../apiTypes";
import type {
  ApiV1SurveyUuidAnomaliesPostRequest,
  ApiV1SurveyUuidAnomaliesPostRequestAnomaliesInner,
  ApiV1SurveyUuidPatchRequest,
  SurveyApi,
  PerSurveyExportSettingsApi,
  ApiV1SurveySettingsGeneralSurveyUuidPatchRequest,
} from "../generated";
import { createAPISlice } from "./createAPISlice";

export default function createSurveyApis(
  surveyApi: SurveyApi,
  perSurveySettingsApi: PerSurveyExportSettingsApi,
) {
  return createAPISlice({
    getSurveys: async () => {
      return await surveyApi.apiV1SurveyGet();
    },
    getSurveyPlaceholders: async () => {
      return await surveyApi.apiV1SurveyPlaceholdersGet();
    },
    getSurveyDetail: async (surveyApiId: string) => {
      return await surveyApi.apiV1SurveyUuidGet(surveyApiId);
    },
    createSurvey: async (params: ApiV1SurveyUuidPatchRequest) => {
      return await surveyApi.apiV1SurveyPost(params);
    },
    updateSurvey: async (
      {
        surveyApiId,
        data,
      }: {
        surveyApiId: string;
        data: ApiV1SurveyUuidPatchRequest;
      },
      abortController?: AbortController,
    ) => {
      return await surveyApi.apiV1SurveyUuidPatch(surveyApiId, data, {
        signal: abortController?.signal,
      });
    },
    deleteSurvey: async (surveyApiId: string) => {
      return await surveyApi.apiV1SurveyUuidDelete(surveyApiId);
    },
    getSurveyPhotos: async (surveyApiId: string) => {
      return await surveyApi.apiV1SurveyUuidPhotosGet(surveyApiId);
    },
    createWalk: async ({
      surveyApiId,
      startedAt,
      endedAt,
      deviceSerialNumber,
      distance,
    }: {
      surveyApiId: string;
      startedAt: Date;
      endedAt?: Date;
      deviceSerialNumber?: string;
      distance?: number;
    }) => {
      return await surveyApi.apiV1SurveyUuidWalkPost(surveyApiId, {
        startedAt: startedAt.toISOString(),
        endedAt: endedAt?.toISOString(),
        deviceSerialNumber,
        distance,
      });
    },
    createAnomaly: async ({
      surveyApiId,
      data,
    }: {
      surveyApiId: string;
      data: ApiV1SurveyUuidAnomaliesPostRequestAnomaliesInner;
    }) => {
      return await surveyApi.apiV1SurveyUuidAnomalyPost(surveyApiId, data);
    },
    createAnomalies: async ({
      surveyApiId,
      data,
    }: {
      surveyApiId: string;
      data: ApiV1SurveyUuidAnomaliesPostRequest["anomalies"];
    }) => {
      return await surveyApi.apiV1SurveyUuidAnomaliesPost(surveyApiId, {
        anomalies: data,
      });
    },
    addPhotos: async ({
      surveyApiId,
      photoFiles,
      latitudes,
      longitudes,
      accuracies,
    }: {
      surveyApiId: string;
      photoFiles: MediaFile[];
      latitudes?: (string | null)[];
      longitudes?: (string | null)[];
      accuracies?: (string | null)[];
    }) => {
      return await surveyApi.apiV1SurveyUuidPhotoPost(
        surveyApiId,
        // @ts-expect-error swagger generated code doesn't support File type, but it does work with it
        photoFiles,
        undefined,
        undefined,
        undefined,
        latitudes,
        longitudes,
        accuracies,
        {
          transformRequest: (d: unknown) => d,
        },
      );
    },
    deletePhoto: async ({
      surveyApiId,
      photoId,
    }: {
      surveyApiId: string;
      photoId: string;
    }) => {
      return await surveyApi.apiV1SurveyUuidPhotoPhotoIdDelete(
        surveyApiId,
        photoId,
      );
    },
    getPDFReport: async (surveyApiId: string, signal?: AbortSignal) => {
      return await surveyApi.apiV1SurveyUuidReportGet(surveyApiId, { signal });
    },
    getCSVReport: async (surveyApiId: string, categories: CSVCategory[]) => {
      return await surveyApi.apiV1SurveyUuidExportCivil3dGet(
        surveyApiId,
        categories.join(","),
      );
    },
    /* General settings */
    getGeneralReportSettings: async (surveyApiId: string) => {
      return await perSurveySettingsApi.apiV1SurveySettingsGeneralSurveyUuidGet(
        surveyApiId,
      );
    },
    patchGeneralReportSettings: async (params: {
      surveyApiId: string;
      data: ApiV1SurveySettingsGeneralSurveyUuidPatchRequest;
    }) => {
      return await perSurveySettingsApi.apiV1SurveySettingsGeneralSurveyUuidPatch(
        params.surveyApiId,
        params.data,
      );
    },
    /* Technical attachments */
    getTechnicalAttachments: async (surveyApiId: string) => {
      return await perSurveySettingsApi.apiV1SurveySettingsAttachmentsSurveyUuidGet(
        surveyApiId,
      );
    },
    postTechnicalAttachment: async ({
      surveyApiId,
      technicalAttachment,
      title,
    }: {
      surveyApiId: string;
      technicalAttachment: MediaFile;
      title: string;
    }) => {
      return await perSurveySettingsApi.apiV1SurveySettingsAttachmentsSurveyUuidPost(
        surveyApiId,
        // @ts-expect-error swagger generated code doesn't support File type, but it does work with it
        technicalAttachment,
        title,
      );
    },
    patchTechnicalAttachment: async ({
      surveyApiId,
      technicalAttachmentApiId,
      data,
    }: {
      surveyApiId: string;
      technicalAttachmentApiId: string;
      data: { title: string; active?: boolean };
    }) => {
      return await perSurveySettingsApi.apiV1SurveySettingsAttachmentsSurveyUuidAttachmentIdPatch(
        surveyApiId,
        technicalAttachmentApiId,
        data,
      );
    },
    deleteTechnicalAttachment: async (
      surveyApiId: string,
      technicalAttachmentApiId: string,
    ) => {
      return await perSurveySettingsApi.apiV1SurveySettingsAttachmentsSurveyUuidAttachmentIdDelete(
        surveyApiId,
        technicalAttachmentApiId,
      );
    },
  });
}
